import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Plus, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function SurveysList() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchSurveys(); }, []);

  const fetchSurveys = async () => {
    try {
      const res = await fetch(`${API}/surveys/builder`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSurveys(data.surveys || []);
      }
    } catch (err) {
      console.error('Failed to fetch surveys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/surveys/builder/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: 'Untitled Survey', survey_type: 'voc' })
      });
      if (res.ok) {
        const data = await res.json();
        navigate(`/survey-builder/${data.survey_id}`);
      } else {
        toast.error('Failed to create survey');
      }
    } catch (err) {
      toast.error('Failed to create survey');
    } finally {
      setCreating(false);
    }
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'paused') return 'bg-amber-100 text-amber-700';
    if (s === 'archived') return 'bg-gray-100 text-gray-500';
    return 'bg-blue-100 text-blue-700';
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="surveys-list-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Surveys</h1>
            <p className="text-sm text-[#64748B] mt-1">Build and manage your feedback surveys</p>
          </div>
          <Button onClick={handleCreate} disabled={creating} className="bg-[#1A3E6F] hover:bg-[#15345C] text-white" data-testid="create-survey-btn">
            <Plus className="w-4 h-4 mr-2" />
            {creating ? 'Creating...' : 'Create New Survey'}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#1A3E6F] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : surveys.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-[#E2E8F0]">
            <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0F172A] mb-2">No surveys yet</h3>
            <p className="text-[#64748B] mb-6">Create your first survey to start collecting feedback</p>
            <Button onClick={handleCreate} disabled={creating} className="bg-[#1A3E6F] hover:bg-[#15345C] text-white">
              <Plus className="w-4 h-4 mr-2" /> Create Survey
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {surveys.map((s) => (
              <div
                key={s.survey_id}
                onClick={() => navigate(`/survey-builder/${s.survey_id}`)}
                className="bg-white rounded-xl border border-[#E2E8F0] p-4 sm:p-5 flex items-center justify-between gap-3 cursor-pointer hover:border-[#1A3E6F]/30 hover:shadow-sm transition-all"
                data-testid={`survey-item-${s.survey_id}`}
              >
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#1A3E6F]/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#1A3E6F]" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-[#0F172A] break-words">{s.name}</h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-[#64748B]">
                      <span className={`px-2 py-0.5 rounded-full font-medium ${statusColor(s.status)}`}>{s.status}</span>
                      <span>{s.question_count} questions</span>
                      <span>{s.assigned_branch_count} branches</span>
                      <span>Updated {formatDate(s.updated_at)}</span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 shrink-0 text-[#94A3B8]" />
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
